/**
 * MagicBI Design System - Button Component
 *
 * Comprehensive button component with multiple variants,
 * sizes, states, and accessibility features.
 */

import React, { forwardRef } from "react";
import styled, { css } from "styled-components";
import { typographyMixin } from "../../../tokens/typography";
import { rawColors } from "../../../tokens/colors";
import { controlSize, fontSizes, spacing } from "@magicbi/ui-library";

// Button variant types
type ButtonVariant =
  | "primary"
  | "primary-dark"
  | "secondary"
  | "secondary-outline"
  | "tertiary"
  | "ghost"
  | "danger"
  | "danger-outline"
  | "link-pill"
  | "dark"
  | "outline"
  | "link";
type ButtonSize = "xs" | "sm" | "md" | "lg";
type ButtonType = "button" | "submit" | "reset";

/** Disabled: dim the enabled look so label stays readable (no grey-on-grey fill). */
const disabledStateStyles = css`
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
    pointer-events: none;
  }
`;

// Button props interface
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button variant */
  variant?: ButtonVariant;
  /** Button size */
  size?: ButtonSize;
  /**
   * Opt-in: a fixed control height (`md` 40 / `sm` 32) with horizontal-only padding and a
   * 1rem icon, instead of the min-height + vertical padding the size scale uses.
   * Only `sm` and `md` change; other sizes ignore it.
   */
  fixedHeight?: boolean;
  /** Whether button is in loading state */
  loading?: boolean;
  /** Whether button is disabled */
  disabled?: boolean;
  /** Full width button */
  fullWidth?: boolean;
  /** Icon to display before text */
  iconBefore?: React.ReactNode;
  /** Icon to display after text */
  iconAfter?: React.ReactNode;
  /** Icon only button (no text) */
  iconOnly?: boolean;
  /** Button type */
  type?: ButtonType;
  /** Custom as prop for polymorphic behavior */
  as?: keyof JSX.IntrinsicElements | React.ComponentType<any>;
  /** Children elements */
  children?: React.ReactNode;
}

// Button variant styles
const getVariantStyles = (variant: ButtonVariant) => {
  switch (variant) {
    case "primary":
      return css`
        background-color: ${({ theme }) => theme.colors.interactive.primary};
        border: 1px solid ${({ theme }) => theme.colors.interactive.primary};
        color: ${({ theme }) => theme.colors.text.inverse};
        box-shadow: ${({ theme }) => theme.shadows.component.button.default};

        &:hover:not(:disabled) {
          background-color: ${({ theme }) => theme.colors.interactive.primaryHover};
          border-color: ${({ theme }) => theme.colors.interactive.primaryHover};
          box-shadow: ${({ theme }) => theme.shadows.component.button.hover};
          transform: translateY(-1px);
        }

        &:active:not(:disabled) {
          background-color: ${({ theme }) => theme.colors.interactive.primaryActive};
          border-color: ${({ theme }) => theme.colors.interactive.primaryActive};
          box-shadow: ${({ theme }) => theme.shadows.component.button.active};
          transform: translateY(0);
        }

        &:focus-visible {
          outline: none;
          box-shadow: ${({ theme }) => theme.shadows.interactive.focus}, ${({ theme }) => theme.shadows.component.button.default};
        }

        ${disabledStateStyles}
      `;

    case "secondary":
      return css`
        background-color: ${({ theme }) => theme.colors.interactive.secondary};
        border: 1px solid ${({ theme }) => theme.colors.border.default};
        color: ${({ theme }) => theme.colors.text.primary};
        box-shadow: ${({ theme }) => theme.shadows.component.button.default};

        &:hover:not(:disabled) {
          background-color: ${({ theme }) => theme.colors.interactive.secondaryHover};
          border-color: ${({ theme }) => theme.colors.border.strong};
          box-shadow: ${({ theme }) => theme.shadows.component.button.hover};
          transform: translateY(-1px);
        }

        &:active:not(:disabled) {
          background-color: ${({ theme }) => theme.colors.interactive.secondaryActive};
          border-color: ${({ theme }) => theme.colors.interactive.primary};
          box-shadow: ${({ theme }) => theme.shadows.component.button.active};
          transform: translateY(0);
        }

        &:focus-visible {
          outline: none;
          box-shadow: ${({ theme }) => theme.shadows.interactive.focus}, ${({ theme }) => theme.shadows.component.button.default};
        }

        ${disabledStateStyles}
      `;

    case "tertiary":
      return css`
        background-color: ${({ theme }) => theme.colors.interactive.tertiary};
        border: 1px solid transparent;
        color: ${({ theme }) => theme.colors.text.primary};
        box-shadow: none;

        &:hover:not(:disabled) {
          background-color: ${({ theme }) => theme.colors.interactive.tertiaryHover};
          color: ${({ theme }) => theme.colors.text.primary};
        }

        &:active:not(:disabled) {
          background-color: ${({ theme }) => theme.colors.interactive.tertiaryActive};
        }

        &:focus-visible {
          outline: none;
          box-shadow: ${({ theme }) => theme.shadows.interactive.focus};
        }

        ${disabledStateStyles}
      `;

    case "ghost":
      return css`
        background-color: transparent;
        border: 1px solid transparent;
        color: ${({ theme }) => theme.colors.text.secondary};
        box-shadow: none;

        &:hover:not(:disabled) {
          background-color: ${({ theme }) => theme.colors.interactive.tertiaryHover};
          color: ${({ theme }) => theme.colors.text.primary};
        }

        &:active:not(:disabled) {
          background-color: ${({ theme }) => theme.colors.interactive.tertiaryActive};
        }

        &:focus-visible {
          outline: none;
          box-shadow: ${({ theme }) => theme.shadows.interactive.focus};
        }

        ${disabledStateStyles}
      `;

    case "secondary-outline":
      // The Cancel half of a form-modal footer — the counterpart to
      // `primary-dark` below. Figma: white fill, `structural/200` hairline,
      // medium-emphasis label.
      //
      // Distinct from `ghost` (transparent border, only visible on hover) and
      // from `secondary` (elevation shadow + translateY lift on hover), neither
      // of which is a resting outline.
      return css`
        background-color: ${rawColors.structural.white};
        border: 1px solid ${rawColors.structural[200]};
        color: ${({ theme }) => theme.colors.text.secondary};
        box-shadow: none;

        &:hover:not(:disabled) {
          background-color: ${rawColors.structural[100]};
          color: ${({ theme }) => theme.colors.text.primary};
        }

        &:active:not(:disabled) {
          background-color: ${rawColors.structural[200]};
        }

        &:focus-visible {
          outline: none;
          box-shadow: ${({ theme }) => theme.shadows.interactive.focus};
        }

        ${disabledStateStyles}
      `;

    case "primary-dark":
      // Dark navy primary CTA (Figma `primary/900` = #041840 + white text).
      // Used by form modals' confirm button (Schedule refresh, etc.).
      return css`
        background-color: ${rawColors.primary[900]};
        border: 1px solid ${rawColors.primary[900]};
        color: ${({ theme }) => theme.colors.text.inverse};
        box-shadow: none;

        &:hover:not(:disabled) {
          background-color: ${rawColors.primary[800]};
          border-color: ${rawColors.primary[800]};
        }

        &:active:not(:disabled) {
          background-color: ${rawColors.primary[700]};
          border-color: ${rawColors.primary[700]};
        }

        &:focus-visible {
          outline: none;
          box-shadow: ${({ theme }) => theme.shadows.interactive.focus};
        }

        ${disabledStateStyles}
      `;

    case "link-pill":
      // Flat outlined affordance for inline/in-card use (e.g. ConnectorCard footer).
      // No shadow, no hover lift — the parent surface owns the interaction signal.
      // Text uses `text.interactive` (design-system blue) — NOT `interactive.primary`
      // (which is brand purple) — to match Figma `text/interactive`.
      return css`
        background-color: ${({ theme }) => theme.colors.surface.primary};
        border: 1px solid ${rawColors.stroke[200]};
        color: ${({ theme }) => theme.colors.text.interactive};
        box-shadow: none;

        &:hover:not(:disabled) {
          background-color: ${({ theme }) => theme.colors.surface.secondary};
        }

        &:active:not(:disabled) {
          background-color: ${({ theme }) => theme.colors.surface.tertiary};
        }

        &:focus-visible {
          outline: none;
          box-shadow: ${({ theme }) => theme.shadows.interactive.focus};
        }

        ${disabledStateStyles}
      `;

    case "danger-outline":
      // Outlined destructive affordance for in-card actions (e.g. Delete on
      // the Connection Detail header). Uses raw `accent.red500` to match the
      // ConnectorStatusBadge convention for hard-danger states — the
      // semantic `error` token currently maps to pink in this design system.
      return css`
        background-color: ${({ theme }) => theme.colors.surface.primary};
        border: 1px solid ${rawColors.accent.red500};
        color: ${rawColors.accent.red500};
        box-shadow: none;

        &:hover:not(:disabled) {
          background-color: ${({ theme }) => theme.colors.surface.secondary};
        }

        &:active:not(:disabled) {
          background-color: ${({ theme }) => theme.colors.surface.tertiary};
        }

        &:focus-visible {
          outline: none;
          box-shadow: ${({ theme }) => theme.shadows.interactive.focus};
        }

        ${disabledStateStyles}
      `;

    case "danger":
      return css`
        background-color: ${({ theme }) => theme.colors.error[500]};
        border: 1px solid ${({ theme }) => theme.colors.error[500]};
        color: ${({ theme }) => theme.colors.text.inverse};
        box-shadow: ${({ theme }) => theme.shadows.component.button.default};

        &:hover:not(:disabled) {
          background-color: ${({ theme }) => theme.colors.error[600]};
          border-color: ${({ theme }) => theme.colors.error[600]};
          box-shadow: ${({ theme }) => theme.shadows.component.button.hover};
          transform: translateY(-1px);
        }

        &:active:not(:disabled) {
          background-color: ${({ theme }) => theme.colors.error[600]};
          border-color: ${({ theme }) => theme.colors.error[600]};
          box-shadow: ${({ theme }) => theme.shadows.component.button.active};
          transform: translateY(0);
        }

        &:focus-visible {
          outline: none;
          box-shadow: ${({ theme }) => theme.shadows.interactive.focus}, ${({ theme }) => theme.shadows.component.button.default};
        }

        ${disabledStateStyles}
      `;

    case "dark":
      // Ink CTA: `background.inverse` fill, light label, no shadow.
      return css`
        background-color: ${({ theme }) => theme.colors.background.inverse};
        border: 1px solid ${({ theme }) => theme.colors.background.inverse};
        color: ${({ theme }) => theme.colors.text.inverse};
        box-shadow: none;

        &:hover:not(:disabled) {
          background-color: ${({ theme }) => theme.colors.background.inverseHover};
          border-color: ${({ theme }) => theme.colors.background.inverseHover};
        }

        &:focus-visible {
          outline: none;
          box-shadow: ${({ theme }) => theme.shadows.interactive.focus};
        }

        ${disabledStateStyles}
      `;

    case "outline":
      // Brand-purple outline: surface fill, `interactive.primary` hairline and label.
      return css`
        background-color: ${({ theme }) => theme.colors.surface.primary};
        border: 1px solid ${({ theme }) => theme.colors.interactive.primary};
        color: ${({ theme }) => theme.colors.interactive.primary};
        box-shadow: none;

        &:hover:not(:disabled) {
          background-color: ${({ theme }) => theme.colors.primary[100]};
        }

        &:focus-visible {
          outline: none;
          box-shadow: ${({ theme }) => theme.shadows.interactive.focus};
        }

        ${disabledStateStyles}
      `;

    case "link":
      // Text-only brand link (`text.link`). Distinct from `ghost`, which is grey.
      return css`
        background-color: transparent;
        border: 1px solid transparent;
        color: ${({ theme }) => theme.colors.text.link};
        box-shadow: none;

        &:hover:not(:disabled) {
          color: ${({ theme }) => theme.colors.text.linkHover};
        }

        &:focus-visible {
          outline: none;
          box-shadow: ${({ theme }) => theme.shadows.interactive.focus};
        }

        ${disabledStateStyles}
      `;

    default:
      return css``;
  }
};

// Button size styles
const getSizeStyles = (size: ButtonSize, iconOnly: boolean = false) => {
  switch (size) {
    case "xs":
      // Caption-3 typography (12px medium) — matches Figma "in-card" affordances.
      return css`
        ${typographyMixin("caption", 3)}
        padding: ${iconOnly ? "6px" : "6px 14px"};
        min-height: 30px;
        ${iconOnly && "width: 30px;"}
        border-radius: 8px;

        svg {
          width: 16px;
          height: 16px;
        }
      `;

    case "sm":
      return css`
        ${typographyMixin("button", "small")}
        padding: ${iconOnly ? "8px" : "8px 12px"};
        min-height: 32px;
        ${iconOnly && "width: 32px;"}
        border-radius: ${({ theme }) => theme.borders.semantic.button.sm};
        
        svg {
          width: 16px;
          height: 16px;
        }
      `;

    case "md":
      return css`
        ${typographyMixin("button", "medium")}
        padding: ${iconOnly ? "12px" : "12px 16px"};
        min-height: 40px;
        ${iconOnly && "width: 40px;"}
        border-radius: ${({ theme }) => theme.borders.semantic.button.md};
        
        svg {
          width: 20px;
          height: 20px;
        }
      `;

    case "lg":
      return css`
        ${typographyMixin("button", "large")}
        padding: ${iconOnly ? "16px" : "16px 24px"};
        min-height: 48px;
        ${iconOnly && "width: 48px;"}
        border-radius: ${({ theme }) => theme.borders.semantic.button.lg};
        
        svg {
          width: 24px;
          height: 24px;
        }
      `;

    default:
      return css``;
  }
};

/** Opt-in `fixedHeight`: exact control height, horizontal padding only, 1rem icon. */
const getFixedHeightStyles = (size: ButtonSize) => {
  if (size === "sm") {
    return css`
      height: ${controlSize.field.sm};
      min-height: 0;
      padding: 0 ${spacing[3]};
      font-size: ${fontSizes["13"]};

      svg {
        width: 1rem;
        height: 1rem;
      }
    `;
  }
  if (size === "md") {
    return css`
      height: ${controlSize.field.md};
      min-height: 0;
      padding: 0 ${spacing[4]};

      svg {
        width: 1rem;
        height: 1rem;
      }
    `;
  }
  return css``;
};

// Styled button component
const StyledButton = styled.button.withConfig({
  shouldForwardProp: (prop) =>
    !["variant", "size", "loading", "fullWidth", "iconOnly", "fixedHeight"].includes(prop),
})<ButtonProps>`
  /* Reset default button styles */
  appearance: none;
  border: none;
  background: none;
  margin: 0;
  cursor: pointer;
  text-decoration: none;
  
  /* Base styles */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.component.button.gap};
  position: relative;
  
  /* Transitions */
  transition: ${({ theme }) => theme.transitions.semantic.interactive.button.all};
  
  /* Prevent text selection */
  user-select: none;
  
  /* Focus styles */
  &:focus {
    outline: none;
  }
  
  /* Full width */
  ${({ fullWidth }) =>
    fullWidth &&
    css`
    width: 100%;
  `}
  
  /* Apply variant styles */
  ${({ variant = "primary" }) => getVariantStyles(variant)}
  
  /* Apply size styles */
  ${({ size = "md", iconOnly = false }) => getSizeStyles(size, iconOnly)}
  
  /* Opt-in fixed height (after size styles so it wins) */
  ${({ fixedHeight, size = "md", iconOnly }) =>
    fixedHeight && !iconOnly ? getFixedHeightStyles(size) : null}

  /* Icon only adjustments */
  ${({ iconOnly, size = "md" }) =>
    iconOnly &&
    css`
    padding: ${size === "sm" ? "8px" : size === "lg" ? "16px" : "12px"};
  `}
  
  /* Loading: hide label but keep a visible spinner (currentColor was invisible with color: transparent) */
  ${({ loading, variant = "primary", theme }) =>
    loading &&
    css`
      cursor: not-allowed;
      color: transparent;

      svg {
        opacity: 0;
      }

      &::after {
        content: "";
        position: absolute;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        animation: button-loading-spin 0.85s linear infinite;
        border: 2px solid
          ${
            ["primary", "danger"].includes(variant)
              ? "rgba(255, 255, 255, 0.35)"
              : theme.colors.border.default
          };
        border-top-color: ${
          ["primary", "danger"].includes(variant)
            ? theme.colors.text.inverse
            : theme.colors.text.primary
        };
      }

      @keyframes button-loading-spin {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }
    `}
`;

// Icon wrapper for proper spacing
const IconWrapper = styled.span<{ position: "before" | "after" }>`
  display: inline-flex;
  align-items: center;
  line-height: 0;
`;

// Button component
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      type = "button",
      loading = false,
      disabled = false,
      fullWidth = false,
      fixedHeight = false,
      iconBefore,
      iconAfter,
      iconOnly = false,
      children,
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;
    // When polymorphic (`as` is set to non-button), don't forward `type`/`disabled` —
    // those are not valid attributes on span/a/etc and trigger React warnings.
    const polymorphedAs = (props as { as?: unknown }).as;
    const isNativeButton = !polymorphedAs || polymorphedAs === "button";

    return (
      <StyledButton
        ref={ref}
        {...(isNativeButton ? { type, disabled: isDisabled } : {})}
        variant={variant}
        size={size}
        loading={loading}
        fullWidth={fullWidth}
        iconOnly={iconOnly}
        fixedHeight={fixedHeight}
        aria-busy={loading || undefined}
        aria-disabled={!isNativeButton && isDisabled ? true : undefined}
        {...props}
      >
        {iconBefore && !iconOnly && (
          <IconWrapper position="before">{iconBefore}</IconWrapper>
        )}

        {iconOnly ? iconBefore || iconAfter || children : children}

        {iconAfter && !iconOnly && (
          <IconWrapper position="after">{iconAfter}</IconWrapper>
        )}
      </StyledButton>
    );
  },
);

Button.displayName = "Button";

export default Button;
